"use client";

import { useEffect, useRef } from "react";
import { oauthConnectionService } from "@/app/_module/services/oauthConnectionService";

/**
 * TimezoneManager - Ensures the user's timezone is detected and saved
 *
 * This component runs on mount and:
 * 1. Detects the user's browser timezone
 * 2. Checks if it's already saved in preferences
 * 3. Saves it if not set or if it has changed
 *
 * This ensures calendar sync, appointment display, and all time-related
 * features use the correct timezone for the agent.
 */
export function TimezoneManager() {
  const hasChecked = useRef(false);

  useEffect(() => {
    // Only run once per session
    if (hasChecked.current) return;
    hasChecked.current = true;

    const ensureTimezoneSet = async () => {
      try {
        // Detect browser timezone
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!detectedTimezone) return;

        // Get current preferences
        const preferences = await oauthConnectionService.getCalendarPreferences();

        // Save timezone if:
        // 1. No timezone is set, OR
        // 2. Timezone has changed (user moved or is traveling)
        if (!preferences?.timezone || preferences.timezone !== detectedTimezone) {
          await oauthConnectionService.updateCalendarPreferences({
            timezone: detectedTimezone,
          });

          // Store in sessionStorage so we don't need to re-check this session
          sessionStorage.setItem('userTimezone', detectedTimezone);
          sessionStorage.setItem('timezoneLastUpdated', new Date().toISOString());
        }
      } catch {
        // Silently fail - this is a background operation
        // User can still use the app, timezone will just use default
      }
    };

    // Check if we already verified timezone this session
    const cachedTimezone = sessionStorage.getItem('userTimezone');
    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (cachedTimezone === currentTimezone) {
      // Timezone hasn't changed this session, skip API call
      return;
    }

    ensureTimezoneSet();
  }, []);

  // This component renders nothing - it's purely for side effects
  return null;
}

export default TimezoneManager;
